import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, User, Clock, Share2, Facebook, Twitter, Linkedin, ArrowRight, Shield } from 'lucide-react';
import { blogPosts, BlogPost } from '../data/blogData';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/ui/LanguageSelector';

const LashingPlanBlog: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [activePost, setActivePost] = useState<BlogPost | null>(null);

  useEffect(() => {
    if (id) {
      const post = blogPosts.find(p => p.id === id);
      if (post) {
        setActivePost(post);
        window.scrollTo(0, 0);
      } else {
        navigate('/lashing-plan-blog');
      }
    } else {
      setActivePost(null);
    }
  }, [id, navigate]);

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-orange-500/30 overflow-x-hidden font-sans">
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-orange-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/5 rounded-full blur-[100px]" />
      </div>

      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/40 backdrop-blur-xl border-b border-white/5 py-4 px-6 md:px-12 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Link to="/" className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20">
            <Shield className="text-white w-6 h-6" />
          </Link>
          <span className="text-xl font-bold tracking-tight">Lashing<span className="text-orange-500">Plan</span><span className="ml-2 px-2 py-0.5 bg-white/5 border border-white/10 rounded-md text-[10px] uppercase tracking-widest text-orange-400 font-black">Blog</span></span>
        </div>
        <div className="flex items-center gap-4">
          <LanguageSelector />
          <Link 
            to="/" 
            className="text-sm font-bold text-slate-400 hover:text-white transition-colors flex items-center gap-2 group"
          >
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            {t('blog.nav.back')}
          </Link>
        </div>
      </nav>

      <main className="relative z-10 pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto">
        <AnimatePresence mode="wait">
          {activePost ? (
            <motion.div
              key="post-detail"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="max-w-4xl mx-auto"
            >
              {/* Post Header */}
              <div className="mb-12">
                <div className="flex items-center gap-3 text-orange-500 text-xs font-bold tracking-widest uppercase mb-6">
                  <span className="text-slate-500 uppercase tracking-widest">{activePost.category}</span>
                  <span className="w-1 h-1 rounded-full bg-slate-700" />
                  <span className="text-slate-500">{activePost.readTime} {t('blog.article.read_time')}</span>
                </div>
                <h1 className="text-4xl md:text-6xl font-black mb-8 leading-tight tracking-tighter">
                  {typeof activePost.title === 'string' ? activePost.title : (lang === 'tr' ? (activePost.title.tr || activePost.title.en) : activePost.title.en)}
                </h1>
                
                <div className="flex flex-wrap items-center gap-6 text-slate-400 border-y border-white/5 py-6">
                   <div className="flex items-center gap-2">
                     <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400"><User className="w-5 h-5"/></div>
                     <span className="text-sm font-semibold text-slate-200">{activePost.author}</span>
                   </div>
                   <div className="flex items-center gap-2">
                     <Clock className="w-4 h-4" />
                     <span className="text-sm">{activePost.readTime}</span>
                   </div>
                   <div className="flex items-center gap-2 ml-auto">
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-600 mr-2">{t('blog.article.share')}</span>
                      <Share2 className="w-4 h-4 cursor-pointer hover:text-orange-400 transition-colors" />
                   </div>
                </div>
              </div>

              {/* Large Image */}
              <div className="flex-1">
                  <img 
                    src={activePost.image} 
                    alt={typeof activePost.title === 'string' ? activePost.title : (lang === 'tr' ? (activePost.title.tr || activePost.title.en) : activePost.title.en)} 
                    className="w-full h-[300px] md:h-[500px] object-cover rounded-[40px] shadow-2xl shadow-orange-500/10 border border-white/5" 
                  />
                </div>

              {/* Article Content */}
              <article 
                className="prose prose-invert prose-orange max-w-none prose-p:text-slate-300 prose-p:leading-relaxed prose-headings:font-black prose-headings:tracking-tight prose-a:text-orange-500"
                dangerouslySetInnerHTML={{ __html: typeof activePost.content === 'string' ? activePost.content : (lang === 'tr' ? (activePost.content.tr || activePost.content.en) : activePost.content.en) }} 
              />

              {/* CTA */}
                <div className="mt-16 p-8 md:p-12 rounded-[32px] bg-gradient-to-br from-orange-500 to-orange-600 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl group-hover:scale-110 transition-transform duration-700" />
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="max-w-md text-center md:text-left">
                      <h3 className="text-2xl font-black mb-4">{t('blog.article.cta_title')}</h3>
                      <p className="text-white/80 font-medium leading-relaxed">
                        {t('blog.article.cta_desc')}
                      </p>
                    </div>
                    <Link 
                      to="/lashing-calculator" 
                      className="px-8 py-4 bg-white text-orange-600 rounded-2xl font-black flex items-center gap-2 hover:scale-105 active:scale-95 transition-all shadow-xl"
                    >
                      {t('blog.article.cta_button')}
                      <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
            </motion.div>
          ) : (
            <motion.div
              key="blog-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="max-w-7xl mx-auto"
            >
              <header className="mb-20 relative px-2 bg-[#050505]">
                 <h1 className="text-5xl md:text-7xl font-black mb-8 tracking-tighter italic">
                   {t('blog.header.title_1')}{' '}
                   <span className="text-transparent bg-clip-text bg-gradient-to-br from-orange-400 to-orange-600">
                     {t('blog.header.title_2')}
                   </span>
                 </h1>
                 <p className="text-xl text-slate-400 max-w-2xl font-medium leading-relaxed">
                   {t('blog.header.subtitle')}
                 </p>
              </header>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogPosts.map((post, idx) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ y: -8 }}
                    className="group"
                  >
                    <Link to={`/lashing-plan-blog/${post.id}`} className="block h-full flex flex-col">
                      <div className="aspect-[16/10] rounded-[32px] overflow-hidden mb-6 border border-white/5 bg-slate-950">
                        <img 
                        src={post.image} 
                        alt={typeof post.title === 'string' ? post.title : (lang === 'tr' ? (post.title.tr || post.title.en) : post.title.en)} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 brightness-75 group-hover:brightness-100" 
                      />
                    </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-4 text-xs font-bold text-orange-500 mb-3 tracking-widest uppercase">
                          <span>{post.category}</span>
                          <span className="w-1 h-1 rounded-full bg-slate-700" />
                          <span className="text-slate-500">{post.readTime}</span>
                        </div>
                        <h3 className="text-2xl font-black mb-4 group-hover:text-orange-400 transition-colors leading-tight">
                          {typeof post.title === 'string' ? post.title : (lang === 'tr' ? (post.title.tr || post.title.en) : post.title.en)}
                        </h3>
                        <p className="text-slate-400 line-clamp-3 mb-6 font-medium leading-relaxed">
                          {typeof post.excerpt === 'string' ? post.excerpt : (lang === 'tr' ? (post.excerpt.tr || post.excerpt.en) : post.excerpt.en)}
                        </p>
                        <div className="flex items-center gap-2 text-sm font-bold text-orange-500 group-hover:gap-4 transition-all">
                          {t('blog.article.read_more')}
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer className="py-24 border-t border-white/5 px-6 md:px-12">
         <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-slate-600 text-[10px] font-black tracking-widest uppercase">
            <div>{t('blog.footer.copyright')}</div>
            <div className="flex items-center gap-8">
              <span>{t('hub.footer.follow')}</span>
              <div className="flex gap-4">
                <Twitter className="w-5 h-5 cursor-pointer hover:text-white transition-colors" />
                <Linkedin className="w-5 h-5 cursor-pointer hover:text-white transition-colors" />
                <Facebook className="w-5 h-5 cursor-pointer hover:text-white transition-colors" />
              </div>
            </div>
         </div>
      </footer>
    </div>
  );
};

export default LashingPlanBlog;
